import React, { Component } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { addDays, compareDesc } from 'date-fns';
import CloseIcon from '@material-ui/icons/Close';
import PropTypes from 'prop-types';
import i18n from '../../i18n';

import {
  formatDateOnlyStringFromLocalTime,
  getLocalDateFromDateString,
  hasContractOverlaps
} from '../../helpers/helpers';

import '../../assets/stylesheets/EmployeeAttributes.css';

const initialState = {
  invalidContractState: false,
  isOpen: false,
  contracts: [],
  temporarilyRemovedContracts: []
};
class ContractEditorModal extends Component {
  constructor(props) {
    super(props);
    const contracts = props.contracts.length
      ? props.contracts
      : [];
    this.state = { ...initialState, contracts };
  }

  componentDidMount = () => {
    const { contracts } = this.props;
    this.formatContracts(contracts);
  }
    

  addContract = () => {
    const { positionId } = this.props;
    this.setState(
      prevState => {
        const newContract = {
          positionId,
          contractStart: new Date(),
          contractEnd: addDays(new Date(), 1)
        };
        const updatedContracts = [...prevState.contracts];
        updatedContracts.push(newContract);
        return { contracts: updatedContracts };
      },
      () => this.validateContractState()
    );
  };

  // contracts arrive with date-strings, here they are converted to date objects
  formatContracts = contracts => {
    const updatedContracts = [];
    contracts.forEach(contract => {
      updatedContracts.push({
        ...contract,
        positionId: contract.positionId,
        contractStart: getLocalDateFromDateString(contract.contractStart),
        contractEnd: getLocalDateFromDateString(contract.contractEnd)
      });
    });
    this.setState({ contracts: updatedContracts }, () =>
      this.validateContractState()
    );
  };

  // contract dates converted back to date strings
  formatContractsForSave = contracts => {
    const formattedContracts = [];
    contracts.forEach(contract => {
      formattedContracts.push({
        ...contract,
        contractStart: formatDateOnlyStringFromLocalTime(
          contract.contractStart
        ),
        contractEnd: formatDateOnlyStringFromLocalTime(contract.contractEnd)
      });
    });
    return formattedContracts;
  };

  // reset the state of the contracts
  handleCancel = () => {
    const { contracts } = this.props;
    this.setState({ temporarilyRemovedContracts: [] }, () =>
      this.formatContracts(contracts)
    );
    this.hideDialog();
  };

  // determine if there are contracts to delete and or save
  handleSave = () => {
    const { contracts, temporarilyRemovedContracts } = this.state;
    const { handleSaveContracts, handleDeleteContracts } = this.props;
    if (temporarilyRemovedContracts.length) {
      handleDeleteContracts(temporarilyRemovedContracts);
    }
    if (contracts.length) {
      const contractsToSave = this.formatContractsForSave(contracts);
      handleSaveContracts(contractsToSave);
    }
    this.hideDialog();
  };

  // this is used to enable or disable the save button
  validateContractState = () => {
    const { contracts } = this.state;
    let updatedInvalidContractState = false;
    // the user is trying to delete an exisiting contract
    if (!contracts.length) {
      updatedInvalidContractState = false;
    }
    contracts.forEach(contract => {
      if (contract.positionId < 1) {
        updatedInvalidContractState = true;
      }
      if (compareDesc(contract.contractStart, contract.contractEnd) < 1) {
        updatedInvalidContractState = true;
      }
    });
    if(!updatedInvalidContractState) {
      updatedInvalidContractState = hasContractOverlaps(contracts);
    }
    this.setState({ invalidContractState: updatedInvalidContractState });
  };

  hideDialog = () => {
    this.setState({ isOpen: false });
  };

  // if this is an existing contract, we should take note to delete it from the db
  removeContract = index => {
    this.setState(
      prevState => {
        const updatedContracts = [...prevState.contracts];
        const contractToRemove = updatedContracts[index];
        const updatedTemporaryContractsToRemove = [...prevState.temporarilyRemovedContracts];
        if(contractToRemove.id) {
          updatedTemporaryContractsToRemove.push(contractToRemove);
        }
        updatedContracts.splice(index, 1);
        return {
          contracts: updatedContracts,
          temporarilyRemovedContracts: updatedTemporaryContractsToRemove
        };
      },
      () => this.validateContractState()
    );
  };

  resetDialog = () => this.setState({ ...initialState });

  showDialog = () => {
    this.setState({ isOpen: true });
  };

  mapContracts = (contract, index) => {
    const { positionLabel } = this.props;
    return (
      <React.Fragment key={`contract-${index}`}>
        <TableRow>
          <TableCell className="attributeLabel">
            {i18n.t('userProfile:position')}
          </TableCell>
          <TableCell>
            {positionLabel}
          </TableCell>
          <TableCell>
            <Button
              className="ssenseButton ssenseDefault"
              onClick={() => this.removeContract(index)}
            >
              {i18n.t('userProfile:removeContract')}
            </Button>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="attributeLabel">
            {i18n.t('userProfile:contractStart')}
          </TableCell>
          <TableCell>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                value={contract.contractStart}
                onChange={date => this.updateContractStart(date, index)}
                format="d MMM yyyy"
              />
            </MuiPickersUtilsProvider>
          </TableCell>
          <TableCell />
        </TableRow>
        <TableRow>
          <TableCell className="attributeLabel">
            {i18n.t('userProfile:contractEnd')}
          </TableCell>
          <TableCell>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                value={contract.contractEnd}
                onChange={date => this.updateContractEnd(date, index)}
                format="d MMM yyyy"
                minDate={addDays(contract.contractStart, 1)}
              />
            </MuiPickersUtilsProvider>
          </TableCell>
          <TableCell />
        </TableRow>
      </React.Fragment>
    );
  };

  updateContractStart = (date, index) => {
    this.setState(
      prevState => {
        const updatedContracts = [...prevState.contracts];
        updatedContracts[index].contractStart = date;
        return { contracts: updatedContracts };
      },
      () => this.validateContractState()
    );
  };

  updateContractEnd = (date, index) => {
    this.setState(
      prevState => {
        const updatedContracts = [...prevState.contracts];
        updatedContracts[index].contractEnd = date;
        return { contracts: updatedContracts };
      },
      () => this.validateContractState()
    );
  };

  render() {
    const { invalidContractState, isOpen } = this.state;
    const { contracts } = this.state;

    return (
      <Dialog
        open={isOpen}
        onBackdropClick={this.hideDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle disableTypography>
          <h4>{i18n.t('userProfile:manageContracts')}</h4>
          <IconButton
            aria-label="close"
            className="modalButtonClose"
            onClick={this.handleCancel}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent scroll="body">
          <Table>
            <TableBody>
              {contracts.map(this.mapContracts)}
              <TableRow>
                <TableCell>
                  <Button
                    className="ssenseButton ssenseSecondary"
                    onClick={this.addContract}
                    disabled={invalidContractState}
                  >
                    {i18n.t('userProfile:addAnotherContract')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Grid
            container
            alignItems="flex-end"
            justify="flex-end"
            className="createEditProductCreateRow"
          >
            <Grid item>
              <Button
                className="ssenseButton ssensePrimary"
                onClick={this.handleSave}
                disabled={invalidContractState}
              >
                {i18n.t('save')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className="ssenseButton ssenseDefault"
                onClick={this.handleCancel}
              >
                {i18n.t('cancel')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }
}

ContractEditorModal.propTypes = {
  contracts: PropTypes.arrayOf(PropTypes.object).isRequired,
  positionLabel: PropTypes.string.isRequired,
  positionId: PropTypes.number.isRequired,
  handleSaveContracts: PropTypes.func.isRequired,
  handleDeleteContracts: PropTypes.func.isRequired
};

export default ContractEditorModal;
